import { useState, useCallback } from "react";
import {
  LocationsPayload,
  LocationType,
} from "../../../../components/filters/Locations/types";
import { LOCATION_TYPES } from "../../../../components/filters/Locations/constants";
import { SAMConfig } from "../../../../types/workspace";

interface UseLocationStateProps {
  samConfiguration: SAMConfig;
  onUpdate: (geography: SAMConfig["geography"]) => void;
}

interface UseLocationStateReturn {
  includeLocations: LocationsPayload;
  excludeLocations: LocationsPayload;
  handleIncludeLocationsChange: (payload: LocationsPayload) => void;
  handleExcludeLocationsChange: (excludedCountryIds: string[]) => void;
  overrideWithIcpData: (countryIds: string[]) => void;
  mergeWithIcpData: (countryIds: string[]) => void;
}

/**
 * Custom hook to manage location state for SAM configuration
 * This keeps track of location type explicitly to avoid inference issues
 */
export function useLocationState({
  samConfiguration,
  onUpdate,
}: UseLocationStateProps): UseLocationStateReturn {
  // Determine initial location type based on existing data
  const getInitialLocationType = (): LocationType => {
    const { geography } = samConfiguration;
    if (geography.stateIds.length > 0) return LOCATION_TYPES.STATE;
    if (geography.regionsIds.length > 0) return LOCATION_TYPES.REGION;
    return LOCATION_TYPES.COUNTRY;
  };

  // State for include location type
  const [includeLocationType, setIncludeLocationType] = useState<LocationType>(
    getInitialLocationType
  );

  // State for exclude location type (always COUNTRY for now)
  const [excludeLocationType] = useState<LocationType>(LOCATION_TYPES.COUNTRY);

  // Build the include locations payload
  const includeLocations: LocationsPayload = {
    countryAlpha2s: samConfiguration.geography.countryIds,
    regionIds: samConfiguration.geography.regionsIds,
    stateIds: samConfiguration.geography.stateIds,
    selectedLocationType: includeLocationType,
  };

  // Build the exclude locations payload
  const excludeLocations: LocationsPayload = {
    countryAlpha2s: samConfiguration.geography.excludedCountryIds,
    regionIds: [],
    stateIds: [],
    selectedLocationType: excludeLocationType,
  };

  // Handle include location changes
  const handleIncludeLocationsChange = useCallback(
    (payload: LocationsPayload) => {
      // Update the location type state
      setIncludeLocationType(payload.selectedLocationType);

      // Update the store with the new values
      onUpdate({
        ...samConfiguration.geography,
        countryIds: payload.countryAlpha2s,
        regionsIds: payload.regionIds,
        stateIds: payload.stateIds,
      });
    },
    [samConfiguration.geography, onUpdate]
  );

  // Handle exclude location changes
  const handleExcludeLocationsChange = useCallback(
    (excludedCountryIds: string[]) => {
      onUpdate({
        ...samConfiguration.geography,
        excludedCountryIds,
      });
    },
    [samConfiguration.geography, onUpdate]
  );

  // Override with ICP data (changes to COUNTRY type)
  const overrideWithIcpData = useCallback(
    (countryIds: string[]) => {
      // Always switch to COUNTRY type when overriding with country data
      setIncludeLocationType(LOCATION_TYPES.COUNTRY);

      onUpdate({
        ...samConfiguration.geography,
        countryIds,
        regionsIds: [], // Clear regions
        stateIds: [], // Clear states
      });
    },
    [samConfiguration.geography, onUpdate]
  );

  // Merge with ICP data (changes to COUNTRY type)
  const mergeWithIcpData = useCallback(
    (countryIds: string[]) => {
      // Always switch to COUNTRY type when merging country data
      setIncludeLocationType(LOCATION_TYPES.COUNTRY);

      // Merge countries
      const mergedCountries = [
        ...new Set([...samConfiguration.geography.countryIds, ...countryIds]),
      ];

      onUpdate({
        ...samConfiguration.geography,
        countryIds: mergedCountries,
        regionsIds: [], // Clear regions since we're switching to country view
        stateIds: [], // Clear states since we're switching to country view
      });
    },
    [samConfiguration.geography, onUpdate]
  );

  return {
    includeLocations,
    excludeLocations,
    handleIncludeLocationsChange,
    handleExcludeLocationsChange,
    overrideWithIcpData,
    mergeWithIcpData,
  };
}
